import { useMutation } from '@tanstack/react-query';
import { API_STATUS_FAILED } from '@/constants/error-codes';
import { legacyRestFetch } from '@/features/account-settings/legacyRestFetch';
import type { ValidAuthMethod } from '@/shared/types/auth';
import { getDeviceUUID } from '@/shared/utils/cookieUtils';

export const SETUP_TWO_FACTOR_AUTH_SMS_METHOD_STATUS = {
  FAILED: 'failed',
  CHECK_SMS_TO_VERIFY: 'check_sms_to_verify',
} as const;
export type SetupTwoFactorAuthSmsMethodStatus =
  (typeof SETUP_TWO_FACTOR_AUTH_SMS_METHOD_STATUS)[keyof typeof SETUP_TWO_FACTOR_AUTH_SMS_METHOD_STATUS];

export type SetupTwoFactorAuthSmsMethodArgs = {
  phoneNumber: string;
  /**
   * The authentication method the user verification it is them
   * before generating recovery codes. Ex: 'password', 'google', 'microsoft'...
   */
  verificationMethod: ValidAuthMethod | null;
  /**
   * The token the user verification it is them
   * before generating recovery codes.
   */
  verificationToken: string | null;
};

export function useSetupTwoFactorAuthSmsMethod() {
  const { mutateAsync: enableSmsTwoFactorAuth } = useMutation({
    mutationFn: async ({
      phoneNumber,
      verificationMethod,
      verificationToken,
    }: SetupTwoFactorAuthSmsMethodArgs): Promise<SetupTwoFactorAuthSmsMethodStatus> => {
      const body = new FormData();
      body.append('phone_number', phoneNumber.toString());
      body.append('device_uuid', getDeviceUUID());
      if (verificationMethod) {
        body.append('method', verificationMethod);
      }
      if (verificationToken) {
        body.append('token', verificationToken);
      }

      const response = await legacyRestFetch('create_two_factor_settings', {
        method: 'POST',
        body,
      });

      if (response.status !== 200 && response.status !== 400) {
        throw new TwoFactorAuthSmsSetupError('Server error');
      }

      const data = await response.json();

      if (data.status === API_STATUS_FAILED) {
        throw new TwoFactorAuthSmsSetupError(data.message);
      }

      return SETUP_TWO_FACTOR_AUTH_SMS_METHOD_STATUS.CHECK_SMS_TO_VERIFY;
    },
  });

  return {
    enableSmsTwoFactorAuth,
  };
}

class TwoFactorAuthSmsSetupError extends Error {
  name = 'TwoFactorAuthSmsSetupError';
  constructor(reason: string) {
    super(`Two-factor authentication setup error: ${reason}`);
  }
}
